// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { localize } from '@ta/common.i18n';
import { HeadProperties } from '@ta/common.routing';
import { FollowPlacementContext } from '@ta/tracking.interactions';
import { ProfilePublicSocialButtonBlock } from '@ta/social.block-button';
import { MemberBlock, getDisplayName } from '@ta/social.member';
import { ProfileOverflowMenu } from '@ta/social.profile';

import styles from './ProfileSection.less';

type Props = {
  profile: ?any,
  isSingleObject: boolean,
  buttonBlock?: React.Node,
  isMobile: boolean,
  children?: React.Node,
};

const ProfileSection = ({ children, isSingleObject, profile, buttonBlock, isMobile }: Props) => (
  <div className={`${styles.section} ${isSingleObject ? 'ui_card section' : ''}`}>
    {isMobile && profile && (
      <ProfileOverflowMenu
        displayName={getDisplayName(profile)}
        profileUserId={profile.userId}
        isBlocking={profile.blockStatus.isBlocking}
        className={styles.mobile_overflow}
      />
    )}
    <div className={styles.member_buttons_wrap}>
      <div className={styles.member_block_wrap}>{profile && <MemberBlock profile={profile} />}</div>
      <div className={styles.buttons_wrap}>
        {profile && !profile.isMe && <ProfilePublicSocialButtonBlock profile={profile} />}
        {buttonBlock}
      </div>
    </div>
    {children}
  </div>
);

ProfileSection.defaultProps = {
  isSingleObject: false,
  buttonBlock: null,
  children: null,
  isMe: false,
};

type ExternalContainerProps = {|
  isSingleObject: boolean,
  userId: string,
  buttonBlock?: React.Node,
  children?: React.Node,
  isMe: boolean,
  profile: ?any,
|};

type ContainerProps = {|
  ...ExternalContainerProps,
  isMobile: boolean,
|};

const ProfileContainer = ({
  isSingleObject,
  profile,
  children,
  buttonBlock,
  isMobile,
}: ContainerProps) => (
  <FollowPlacementContext.Provider value={{ placement: 'FOLLOW_PROFILE_IN_VIEW' }}>
    <ProfileSection
      isSingleObject={isSingleObject}
      profile={profile}
      buttonBlock={buttonBlock}
      isMobile={isMobile}
    >
      <HeadProperties
        title={
          localize('social_profile_page_title', {
            displayname: getDisplayName(profile),
            username: profile ? `@${profile.username}` : getDisplayName(profile),
          }) /* {displayname} ({username}) - Profile - TripAdvisor */
        }
        metaTags={[
          {
            name: 'description',
            content: localize(
              'social_profile_page_meta_description_v2',
              {
                username: profile ? `@${profile.username}` : getDisplayName(profile),
              } /* See travel reviews, photos, videos, trips, and more contributed by {username} on TripAdvisor. */,
            ),
          },
        ]}
      />
      {children}
    </ProfileSection>
  </FollowPlacementContext.Provider>
);

ProfileContainer.defaultProps = {
  buttonBlock: null,
  children: null,
};

export default connect<ContainerProps, ExternalContainerProps, _, _, _, _>(state => ({
  isMobile: state.meta.device.viewportCategory === 'MOBILE',
}))(ProfileContainer);
